import { useQuery } from "@tanstack/react-query";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { Card, Notice } from "./adminUI";
import BookingStats from "./BookingStats";
import { useBookingSettings } from "@/lib/booking";

function useCount(key, table) {
    return useQuery({
        queryKey: ["count", key],
        enabled: isSupabaseConfigured,
        retry: false,
        staleTime: 60_000,
        queryFn: async () => {
            const { count, error } = await supabase
                .from(table)
                .select("*", { count: "exact", head: true });
            if (error) throw error;
            return count ?? 0;
        },
    });
}

function Stat({ label, value, testid }) {
    return (
        <Card className="text-center">
            <p className="font-display text-4xl font-black text-neon-cyan" data-testid={testid}>
                {value}
            </p>
            <p className="mt-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-dim">
                {label}
            </p>
        </Card>
    );
}

export default function Dashboard({ go }) {
    const tours = useCount("tours", "tours");
    const locations = useCount("locations", "locations");
    const photos = useCount("gallery", "gallery_images");
    const equipment = useCount("equipment", "resources");
    const slots = useCount("slots", "booking_slots");
    const bookingSettings = useBookingSettings();
    const connected = isSupabaseConfigured && !tours.isError;

    return (
        <div className="space-y-8">
            <div>
                <h1 className="font-display text-2xl font-black uppercase sm:text-3xl">
                    Dashboard
                </h1>
                <p className="mt-2 text-sm text-dim">
                    Visão geral do conteúdo do site.
                </p>
            </div>

            {!isSupabaseConfigured ? (
                <Notice tone="red" testid="dashboard-not-configured">
                    Supabase não configurado. Defina as variáveis de ambiente
                    REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY.
                </Notice>
            ) : tours.isError ? (
                <Notice tone="amber" testid="dashboard-db-error">
                    Não foi possível ler o banco. Verifique se o SQL de
                    configuração foi aplicado no Supabase.
                </Notice>
            ) : null}

            <BookingStats />
            <Card>
                <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                    Preparação para reservas reais
                </p>
                <p className="mt-3 text-sm leading-relaxed text-dim">
                    Reservas só devem ser liberadas quando equipamento, agenda e políticas estiverem configurados. Horários da prévia não representam vagas reais.
                </p>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                    <div>Equipamentos cadastrados: <strong className="text-ink">{equipment.data ?? "—"}</strong></div>
                    <div>Horários cadastrados: <strong className="text-ink">{slots.data ?? "—"}</strong></div>
                    <div>Políticas aprovadas: <strong className={bookingSettings.legal_approved?"text-neon-teal":"text-neon-sun"}>{bookingSettings.legal_approved?"Sim":"Pendente"}</strong></div>
                    <div>Pagamento online: <strong className={bookingSettings.online_payment_enabled?"text-neon-teal":"text-neon-sun"}>{bookingSettings.online_payment_enabled?"Ativo":"Desativado"}</strong></div>
                </div>
                <button type="button" className="mt-5 min-h-11 rounded-full border border-neon-cyan/40 px-5 text-sm font-bold text-neon-cyan" onClick={()=>go("disponibilidade")}>Configurar equipamentos e agenda</button>
            </Card>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Stat
                    label="Passeios cadastrados"
                    value={tours.data ?? "—"}
                    testid="dashboard-count-tours"
                />
                <Stat
                    label="Locais"
                    value={locations.data ?? "—"}
                    testid="dashboard-count-locations"
                />
                <Stat
                    label="Fotos"
                    value={photos.data ?? "—"}
                    testid="dashboard-count-photos"
                />
            </div>

            <Card>
                <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                    Status do site
                </p>
                <p className="mt-3 text-sm text-dim">
                    {connected
                        ? "Conectado ao Supabase — as edições do painel aparecem no site público."
                        : "Modo padrão — o site exibe o conteúdo original até o Supabase ser configurado."}
                </p>
            </Card>

            <Card>
                <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                    Atalhos
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                    {[
                        ["reservas", "Ver reservas"],
                        ["disponibilidade", "Agenda"],                        ["passeios", "Editar passeios"],
                        ["precos", "Editar preços"],
                        ["fotos", "Gerenciar fotos"],
                        ["conteudo", "Editar textos"],
                        ["contato", "WhatsApp / Contato"],
                    ].map(([id, label]) => (
                        <button
                            key={id}
                            onClick={() => go(id)}
                            data-testid={`dashboard-shortcut-${id}`}
                            className="rounded-full border border-ink/20 px-5 py-2.5 font-mono2 text-[10px] font-semibold uppercase tracking-[0.2em] text-dim transition-colors hover:border-neon-cyan hover:text-neon-cyan"
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </Card>
        </div>
    );
}
